import { useEffect, useState, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiFetch, supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileSignature, Trash2, ShieldCheck, Scale, Handshake, Download, FileText } from "lucide-react";
import { PageHeader, StatusBadge, StatusTabs, CardActionMenu, EmptyState, formatDate } from "@/lib/ui";
import { Typeahead } from "@/components/Typeahead";
import { BACKEND_URL, supabase as sb } from "@/lib/supabase";
import { saveOrShareBlob, fetchAndSave } from "@/lib/downloads";

const SERVICES = ["Administrative Support", "Virtual Assistant Services", "Calendar Management", "Scheduling", "Email Management", "Data Entry", "Document Management", "Research", "Customer Support", "Project Coordination", "CRM Support", "Intake Coordination", "Other"];
const STATUSES = ["draft", "sent", "signed", "active", "expired", "terminated"];

const emptyForm = () => ({
  contract_name: "Service Agreement",
  parties: { provider_name: "", provider_contact: "", provider_email: "", provider_phone: "", client_name: "", client_contact: "", client_email: "", client_phone: "" },
  services: { included: [], scope: "", deliverables: "", not_included: "" },
  arrangement: { service_type: "hourly", hours_included: "", hours_per_week: "", hours_per_month: "", service_days: "", start_time: "", end_time: "", client_time_zone: "", turnaround: "" },
  communication: { primary_method: "", response_expectations: "", meeting_frequency: "", meeting_schedule: "", additional: "" },
  payment_terms: { billing_method: "", rate: "", billing_frequency: "", estimated_amount: "", payment_due: "", payment_method: "", late_terms: "", additional: "" },
  confidentiality: { agreed: false, additional: "" },
  data_security: { additional: "" },
  client_responsibilities: { accurate_info: true, authorized_access: true, timely_approvals: true, instructions: true, payments: true, additional: "" },
  provider_responsibilities: { professional_service: true, material_issues_notice: true, protect_info: true, perform_services: true, additional: "" },
  intellectual_property: { work_product_owner: "client", additional: "" },
  termination: { notice_period_days: 14, method: "written notice", cancellation: "", additional: "" },
  signature: { full_name: "", title: "", signature: "", signed_at: "" },
  effective_date: "", end_date: "",
});

export default function Contracts() {
  const { currentOrgId } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [client, setClient] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [status, setStatus] = useState("draft");
  const [q, setQ] = useState(params.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(params.get("status") || "all");
  const [finalizedFilter, setFinalizedFilter] = useState(params.get("finalized") || "");

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q); else next.delete("q");
    if (statusFilter && statusFilter !== "all") next.set("status", statusFilter); else next.delete("status");
    if (finalizedFilter) next.set("finalized", finalizedFilter); else next.delete("finalized");
    setParams(next, { replace: true });
    /* eslint-disable-next-line */
  }, [q, statusFilter, finalizedFilter]);

  const load = async () => {
    setLoading(true);
    try { const r = await apiFetch(`/contracts?organization_id=${currentOrgId}`); setRows(r); }
    catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (currentOrgId) load(); /* eslint-disable-next-line */ }, [currentOrgId]);

  // Deep-link: open ?id=xxx contract automatically once rows load
  useEffect(() => {
    const id = params.get("id");
    if (!id || !rows.length || dialogOpen) return;
    const row = rows.find((r) => r.id === id);
    if (row) { openEdit(row); params.delete("id"); setParams(params, { replace: true }); }
    /* eslint-disable-next-line */
  }, [rows, params.get("id")]);

  const openNew = () => { setEditing(null); setClient(null); setForm(emptyForm()); setStatus("draft"); setDialogOpen(true); };

  const openEdit = async (r) => {
    setEditing(r); setClient(r.clients ? { id: r.client_id, ...r.clients } : null);
    const base = emptyForm();
    setForm({
      contract_name: r.contract_name || "Service Agreement",
      parties: { ...base.parties, ...(r.parties || {}) },
      services: { ...base.services, ...(r.services || {}) },
      arrangement: { ...base.arrangement, ...(r.arrangement || {}) },
      communication: { ...base.communication, ...(r.communication || {}) },
      payment_terms: { ...base.payment_terms, ...(r.payment_terms || {}) },
      confidentiality: { ...base.confidentiality, ...(r.confidentiality || {}) },
      data_security: { ...base.data_security, ...(r.data_security || {}) },
      client_responsibilities: { ...base.client_responsibilities, ...(r.client_responsibilities || {}) },
      provider_responsibilities: { ...base.provider_responsibilities, ...(r.provider_responsibilities || {}) },
      intellectual_property: { ...base.intellectual_property, ...(r.intellectual_property || {}) },
      termination: { ...base.termination, ...(r.termination || {}) },
      signature: { ...base.signature, ...(r.signature || {}) },
      effective_date: r.effective_date || "", end_date: r.end_date || "",
    });
    setStatus(r.status || "draft");
    setDialogOpen(true);
  };

  const onSelectClient = async (c) => {
    setClient(c);
    if (!c) return;
    // Prefill parties from client + org
    const { data: org } = await supabase.from("organizations").select("name,billing_email").eq("id", currentOrgId).limit(1).maybeSingle();
    setForm((f) => ({
      ...f,
      parties: {
        ...f.parties,
        provider_name: org?.name || f.parties.provider_name,
        provider_email: org?.billing_email || f.parties.provider_email,
        client_name: c.referral_source || `${c.first_name} ${c.last_name}`,
        client_contact: `${c.first_name} ${c.last_name}`,
        client_email: c.email || "",
        client_phone: c.phone || "",
      },
    }));
  };

  const save = async (newStatus) => {
    if (!client) return toast.error("Please select a client");
    if (newStatus === "signed") {
      if (!form.confidentiality.agreed) return toast.error("Confidentiality acknowledgment is required to sign");
      if (!form.signature.full_name?.trim() || !form.signature.signature?.trim()) return toast.error("Signature name is required");
    }
    const payload = {
      organization_id: currentOrgId, client_id: client.id,
      contract_name: form.contract_name,
      parties: form.parties, services: form.services, arrangement: form.arrangement,
      communication: form.communication, payment_terms: form.payment_terms,
      confidentiality: form.confidentiality, data_security: form.data_security,
      client_responsibilities: form.client_responsibilities,
      provider_responsibilities: form.provider_responsibilities,
      intellectual_property: form.intellectual_property, termination: form.termination,
      signature: newStatus === "signed" ? { ...form.signature, signed_at: form.signature.signed_at || new Date().toISOString() } : form.signature,
      effective_date: form.effective_date || null, end_date: form.end_date || null,
      status: newStatus || status,
    };
    try {
      if (editing) await apiFetch(`/contracts/${editing.id}`, { method: "PATCH", body: JSON.stringify(payload) });
      else await apiFetch(`/contracts`, { method: "POST", body: JSON.stringify(payload) });
      toast.success(newStatus === "signed" ? "Contract signed" : "Contract saved");
      setDialogOpen(false); load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this contract?")) return;
    try { await apiFetch(`/contracts/${r.id}`, { method: "DELETE" }); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const downloadPdf = async (r) => {
    try {
      const { data } = await sb.auth.getSession();
      const token = data?.session?.access_token;
      const filename = `${(r.contract_name || "contract").replace(/\s+/g, "_")}.pdf`;
      await fetchAndSave(`${BACKEND_URL}/api/contracts/${r.id}/pdf`, {
        token, filename,
        options: { title: r.contract_name || "Contract PDF", dialogTitle: "Save or share contract" },
      });
      toast.success("PDF ready");
    } catch (e) { toast.error(e.message || "PDF failed"); }
  };

  const downloadAudit = async (r) => {
    try {
      const { data } = await sb.auth.getSession();
      const token = data?.session?.access_token;
      await fetchAndSave(`${BACKEND_URL}/api/contracts/${r.id}/audit.csv`, {
        token,
        filename: `audit_${(r.contract_number || r.id)}.csv`,
        options: { title: "Contract audit history", dialogTitle: "Save or share audit CSV" },
      });
      toast.success("Audit history ready");
    } catch (e) { toast.error(e.message || "Audit export failed"); }
  };

  const toggleService = (s) => setForm((f) => {
    const cur = f.services.included || [];
    return { ...f, services: { ...f.services, included: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] } };
  });

  // Contract lifecycle — pulled from the actual backend enum.
  const STATUS_TABS = [
    { id: "all",        label: "All" },
    { id: "draft",      label: "Draft" },
    { id: "sent",       label: "Sent" },
    { id: "signed",     label: "Signed" },
    { id: "active",     label: "Active" },
    { id: "expired",    label: "Expired" },
    { id: "terminated", label: "Terminated" },
  ];
  const statusCounts = useMemo(() => {
    const c = { all: rows.length };
    rows.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, [rows]);
  const filtered = useMemo(() => rows.filter((r) => {
    if (statusFilter !== "all" && r.status !== statusFilter) return false;
    if (finalizedFilter === "missing") {
      const inScope = ["signed", "active", "terminated", "expired"].includes(r.status);
      if (!inScope || r.finalized_pdf_url) return false;
    }
    if (q) {
      const ql = q.toLowerCase();
      const clientName = r.clients ? `${r.clients.first_name} ${r.clients.last_name}`.toLowerCase() : "";
      if (!(r.contract_name || "").toLowerCase().includes(ql) && !clientName.includes(ql)) return false;
    }
    return true;
  }), [rows, statusFilter, finalizedFilter, q]);

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="contracts-page">
      <PageHeader title="Service Contracts" subtitle="Draft, sign and store service agreements. Every contract is linked to a client."
        actions={<Button data-testid="new-contract-btn" onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New contract</Button>} />

      {rows.length > 0 && (
        <StatusTabs
          tabs={STATUS_TABS}
          value={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          testidPrefix="contract-status-tab"
          right={
            <input
              data-testid="contract-search"
              placeholder="Search contract or client…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="hidden md:block bg-transparent border-0 outline-none text-sm px-3 min-w-[220px]"
            />
          }
        />
      )}

      {finalizedFilter === "missing" && (
        <div
          className="bg-[#FDDCDC] border border-[#F5B4B4] text-[#B02222] rounded-2xl px-4 py-3 flex items-center gap-3"
          data-testid="contract-finalized-filter-banner"
        >
          <span className="text-sm">
            Showing <b>{filtered.length}</b> contract{filtered.length === 1 ? "" : "s"} that are signed but
            missing a stored authoritative PDF snapshot.
          </span>
          <button
            className="ml-auto text-xs px-3 py-1 rounded-full bg-white border border-[#F5B4B4] hover:bg-[#FEE9E9]"
            onClick={() => setFinalizedFilter("")}
            data-testid="contract-finalized-filter-clear"
          >
            Clear filter
          </button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-[#57534E]">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={FileSignature} title="No contracts yet"
          description="Draft a service contract. Signed contracts show up here for the whole team."
          action={<Button onClick={openNew} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F]"><Plus size={14} className="mr-1.5" /> New contract</Button>} />
      ) : (
        <div className="bg-white rounded-2xl border border-[#E8E2D9] overflow-hidden">
          {/* Desktop table (≥md) */}
          <table className="w-full text-sm hidden md:table">
            <thead className="bg-[#FAF8F5] text-xs uppercase tracking-widest text-[#57534E]">
              <tr>
                <th className="text-left px-4 py-3">Contract</th>
                <th className="text-left px-4 py-3">Client</th>
                <th className="text-left px-4 py-3">Effective</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E2D9]">
              {filtered.map((r) => (
                <tr key={r.id} data-testid={`contract-row-${r.id}`} onClick={() => openEdit(r)} className="hover:bg-[#FAF8F5] cursor-pointer">
                  <td className="px-4 py-3 font-medium">{r.contract_name}</td>
                  <td className="px-4 py-3">
                    {r.clients ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/clients/${r.client_id}`); }}
                        className="text-[#B85C1F] hover:underline"
                        data-testid={`contract-client-link-${r.id}`}
                      >
                        {r.clients.first_name} {r.clients.last_name}
                      </button>
                    ) : <span className="text-[#57534E]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-[#57534E]">{r.effective_date ? formatDate(r.effective_date) : "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={r.status} /></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={(e) => { e.stopPropagation(); downloadPdf(r); }}
                        className="text-[#2C3A29] hover:bg-[#E8F0E4] p-1.5 rounded-lg" title="Download PDF"
                        data-testid={`contract-pdf-${r.id}`}>
                        <Download size={14} />
                      </button>
                      {r.status !== "signed" && r.status !== "active" && (
                        <button onClick={(e) => { e.stopPropagation(); remove(r); }} className="text-[#B02222] hover:bg-[#FDDCDC] p-1.5 rounded-lg" data-testid={`contract-delete-${r.id}`}><Trash2 size={14} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Mobile card list (<md) */}
          <div className="md:hidden divide-y divide-[#E8E2D9]">
            {filtered.map((r) => (
              <div key={r.id} className="flex items-center gap-1 hover:bg-[#FAF8F5] active:bg-[#FDE9D8]/40 transition-colors" data-testid={`contract-card-${r.id}`}>
                <button
                  onClick={() => openEdit(r)}
                  className="flex-1 text-left px-4 py-4 min-w-0"
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold min-w-0 truncate">{r.contract_name}</div>
                    <StatusBadge value={r.status} />
                  </div>
                  {r.clients && (
                    <div className="text-sm text-[#57534E] truncate mt-0.5">{r.clients.first_name} {r.clients.last_name}</div>
                  )}
                  <div className="text-xs text-[#A8A29E] mt-1">
                    {r.effective_date ? `Effective ${formatDate(r.effective_date)}` : "No effective date"}
                  </div>
                </button>
                <CardActionMenu
                  testid={`contract-menu-${r.id}`}
                  actions={[
                    { label: "Download PDF", icon: Download, onClick: () => downloadPdf(r), testid: `menu-pdf-${r.id}` },
                    { label: "Download audit history", icon: FileText, onClick: () => downloadAudit(r), testid: `menu-audit-${r.id}` },
                    { separator: true },
                    { label: "Delete", icon: Trash2, destructive: true, confirmMessage: `Delete contract "${r.contract_name}"? This cannot be undone.`, onClick: () => remove(r), hidden: r.status === "signed" || r.status === "active", testid: `menu-delete-${r.id}` },
                  ]}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">{editing ? "Edit contract" : "New contract"}</DialogTitle>
            <DialogDescription className="text-xs text-[#57534E]">Do not enter passwords, API keys or banking credentials anywhere on this form.</DialogDescription>
          </DialogHeader>

          {editing?.finalized_source === "legacy_backfill" && (
            <div
              data-testid="contract-legacy-backfill-note"
              className="rounded-2xl border border-[#E8D9BA] bg-[#FBF3E4] px-4 py-3 mt-3 text-sm text-[#6B5A1F]"
            >
              <div className="font-semibold text-[#5A4A15]">Legacy snapshot</div>
              <div className="mt-0.5">
                Generated on {editing.finalized_at ? new Date(editing.finalized_at).toLocaleDateString() : "an earlier date"}.
                This PDF was recreated from available historical data and may not be byte-identical
                to the original document shown to the client at signing
                {editing.signed_at ? ` on ${new Date(editing.signed_at).toLocaleDateString()}` : ""}.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-2">
            <Field label="Contract name"><Input data-testid="c-name" value={form.contract_name} onChange={(e) => setForm({ ...form, contract_name: e.target.value })} /></Field>
            <Field label="Client *">
              <Typeahead value={client}
                fetchFn={async (search) => {
                  let q = supabase.from("clients").select("id,first_name,last_name,email,phone,referral_source").eq("organization_id", currentOrgId).limit(8);
                  if (search) q = q.or(`first_name.ilike.%${search}%,last_name.ilike.%${search}%,email.ilike.%${search}%`);
                  const { data } = await q; return data || [];
                }}
                displayFn={(c) => `${c.first_name} ${c.last_name}`}
                secondaryFn={(c) => c.email || ""}
                onChange={onSelectClient} placeholder="Search client…" testid="c-client" />
            </Field>
          </div>

          <Section title="A. Parties" icon={Handshake}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["provider_name", "Service provider name"],
                ["provider_contact", "Provider contact"],
                ["provider_email", "Provider email"],
                ["provider_phone", "Provider phone"],
                ["client_name", "Client name"],
                ["client_contact", "Client primary contact"],
                ["client_email", "Client email"],
                ["client_phone", "Client phone"],
              ].map(([k, l]) => (
                <Field key={k} label={l}><Input value={form.parties[k] || ""} onChange={(e) => setForm((f) => ({ ...f, parties: { ...f.parties, [k]: e.target.value } }))} /></Field>
              ))}
              <Field label="Effective date"><Input type="date" data-testid="c-effective" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} /></Field>
              <Field label="End date"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></Field>
            </div>
          </Section>

          <Section title="B. Services" icon={Scale}>
            <Field label="Services included">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-1">
                {SERVICES.map((s) => (
                  <label key={s} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${(form.services.included || []).includes(s) ? "bg-[#FDE9D8] border-[#F0C69A]" : "bg-white border-[#E8E2D9]"}`}>
                    <input type="checkbox" checked={(form.services.included || []).includes(s)} onChange={() => toggleService(s)} className="accent-[#E8712C]" />{s}
                  </label>
                ))}
              </div>
            </Field>
            <Field label="Detailed scope of services"><Textarea rows={3} value={form.services.scope} onChange={(e) => setForm((f) => ({ ...f, services: { ...f.services, scope: e.target.value } }))} /></Field>
            <Field label="Deliverables"><Textarea rows={2} value={form.services.deliverables} onChange={(e) => setForm((f) => ({ ...f, services: { ...f.services, deliverables: e.target.value } }))} /></Field>
            <Field label="Services not included"><Textarea rows={2} value={form.services.not_included} onChange={(e) => setForm((f) => ({ ...f, services: { ...f.services, not_included: e.target.value } }))} /></Field>
          </Section>

          <Section title="C. Service arrangement" icon={Scale}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Service type">
                <Select value={form.arrangement.service_type} onValueChange={(v) => setForm((f) => ({ ...f, arrangement: { ...f.arrangement, service_type: v } }))}>
                  <SelectTrigger data-testid="c-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hourly">Hourly</SelectItem>
                    <SelectItem value="monthly_retainer">Monthly retainer</SelectItem>
                    <SelectItem value="project_based">Project-based</SelectItem>
                    <SelectItem value="package">Package</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {[
                ["hours_included", "Hours included"],
                ["hours_per_week", "Hours per week"],
                ["hours_per_month", "Hours per month"],
                ["service_days", "Service days"],
                ["start_time", "Start time"],
                ["end_time", "End time"],
                ["client_time_zone", "Client time zone"],
                ["turnaround", "Expected turnaround"],
              ].map(([k, l]) => (
                <Field key={k} label={l}><Input value={form.arrangement[k] || ""} onChange={(e) => setForm((f) => ({ ...f, arrangement: { ...f.arrangement, [k]: e.target.value } }))} /></Field>
              ))}
            </div>
          </Section>

          <Section title="D. Communication" icon={Scale}>
            {[
              ["primary_method", "Primary communication method"],
              ["response_expectations", "Response expectations"],
              ["meeting_frequency", "Meeting frequency"],
              ["meeting_schedule", "Meeting schedule"],
              ["additional", "Additional communication instructions"],
            ].map(([k, l]) => (
              <Field key={k} label={l}><Input value={form.communication[k] || ""} onChange={(e) => setForm((f) => ({ ...f, communication: { ...f.communication, [k]: e.target.value } }))} /></Field>
            ))}
          </Section>

          <Section title="E. Payment terms" icon={Scale}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {[
                ["billing_method", "Billing method"],
                ["rate", "Rate"],
                ["billing_frequency", "Billing frequency"],
                ["estimated_amount", "Estimated amount"],
                ["payment_due", "Payment due"],
                ["payment_method", "Payment method"],
              ].map(([k, l]) => (
                <Field key={k} label={l}><Input value={form.payment_terms[k] || ""} onChange={(e) => setForm((f) => ({ ...f, payment_terms: { ...f.payment_terms, [k]: e.target.value } }))} /></Field>
              ))}
              <Field label="Late payment terms" wide><Textarea rows={2} value={form.payment_terms.late_terms} onChange={(e) => setForm((f) => ({ ...f, payment_terms: { ...f.payment_terms, late_terms: e.target.value } }))} /></Field>
              <Field label="Additional payment terms" wide><Textarea rows={2} value={form.payment_terms.additional} onChange={(e) => setForm((f) => ({ ...f, payment_terms: { ...f.payment_terms, additional: e.target.value } }))} /></Field>
            </div>
          </Section>

          <Section title="F. Confidentiality" icon={ShieldCheck}>
            <p className="text-sm text-[#57534E]">The service provider will protect confidential client and business information disclosed during the engagement, will not share it with third parties without permission, and will use it only to perform the agreed services.</p>
            <label className="flex items-start gap-3 p-3 rounded-xl border border-[#E8E2D9] bg-white cursor-pointer">
              <input type="checkbox" checked={!!form.confidentiality.agreed} onChange={(e) => setForm((f) => ({ ...f, confidentiality: { ...f.confidentiality, agreed: e.target.checked } }))} className="w-4 h-4 mt-0.5 accent-[#E8712C]" data-testid="c-conf-agree" />
              <span className="text-sm">I understand and agree to the confidentiality provisions.</span>
            </label>
            <Field label="Additional confidentiality terms"><Textarea rows={2} value={form.confidentiality.additional} onChange={(e) => setForm((f) => ({ ...f, confidentiality: { ...f.confidentiality, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="G. Data & account security" icon={ShieldCheck}>
            <p className="text-sm text-[#57534E]">The service provider will apply reasonable protections for client information and will access client systems only via authorized channels. <b>Never enter passwords, API keys, banking passwords, or security codes into this contract.</b></p>
            <Field label="Additional data-security terms"><Textarea rows={2} value={form.data_security.additional} onChange={(e) => setForm((f) => ({ ...f, data_security: { ...f.data_security, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="H. Client responsibilities" icon={Handshake}>
            <div className="space-y-1.5">
              {[
                ["accurate_info", "Provide accurate information"],
                ["authorized_access", "Provide authorized access to systems"],
                ["timely_approvals", "Provide timely approvals"],
                ["instructions", "Provide necessary instructions"],
                ["payments", "Pay invoices according to this agreement"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.client_responsibilities[k]} onChange={(e) => setForm((f) => ({ ...f, client_responsibilities: { ...f.client_responsibilities, [k]: e.target.checked } }))} className="w-4 h-4 accent-[#E8712C]" />
                  {l}
                </label>
              ))}
            </div>
            <Field label="Additional client responsibilities"><Textarea rows={2} value={form.client_responsibilities.additional} onChange={(e) => setForm((f) => ({ ...f, client_responsibilities: { ...f.client_responsibilities, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="I. Service provider responsibilities" icon={Handshake}>
            <div className="space-y-1.5">
              {[
                ["professional_service", "Deliver services in a professional manner"],
                ["material_issues_notice", "Communicate material issues promptly"],
                ["protect_info", "Protect confidential client information"],
                ["perform_services", "Perform services as agreed"],
              ].map(([k, l]) => (
                <label key={k} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!form.provider_responsibilities[k]} onChange={(e) => setForm((f) => ({ ...f, provider_responsibilities: { ...f.provider_responsibilities, [k]: e.target.checked } }))} className="w-4 h-4 accent-[#E8712C]" />
                  {l}
                </label>
              ))}
            </div>
            <Field label="Additional provider responsibilities"><Textarea rows={2} value={form.provider_responsibilities.additional} onChange={(e) => setForm((f) => ({ ...f, provider_responsibilities: { ...f.provider_responsibilities, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="J. Intellectual property" icon={Scale}>
            <Field label="Work product ownership">
              <Select value={form.intellectual_property.work_product_owner} onValueChange={(v) => setForm((f) => ({ ...f, intellectual_property: { ...f.intellectual_property, work_product_owner: v } }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client owns work product upon payment</SelectItem>
                  <SelectItem value="provider">Provider retains ownership</SelectItem>
                  <SelectItem value="joint">Joint ownership</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <p className="text-xs text-[#57534E]">Provider's pre-existing materials, templates, and tools remain the property of the provider.</p>
            <Field label="Additional IP terms"><Textarea rows={2} value={form.intellectual_property.additional} onChange={(e) => setForm((f) => ({ ...f, intellectual_property: { ...f.intellectual_property, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="K. Termination" icon={Scale}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <Field label="Required notice period (days)"><Input type="number" min={0} value={form.termination.notice_period_days} onChange={(e) => setForm((f) => ({ ...f, termination: { ...f.termination, notice_period_days: Number(e.target.value) } }))} /></Field>
              <Field label="Termination method"><Input value={form.termination.method} onChange={(e) => setForm((f) => ({ ...f, termination: { ...f.termination, method: e.target.value } }))} /></Field>
              <Field label="Cancellation terms"><Input value={form.termination.cancellation} onChange={(e) => setForm((f) => ({ ...f, termination: { ...f.termination, cancellation: e.target.value } }))} /></Field>
            </div>
            <Field label="Additional termination terms"><Textarea rows={2} value={form.termination.additional} onChange={(e) => setForm((f) => ({ ...f, termination: { ...f.termination, additional: e.target.value } }))} /></Field>
          </Section>

          <Section title="L. Client signature" icon={FileSignature}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Full legal name"><Input data-testid="c-sig-name" value={form.signature.full_name} onChange={(e) => setForm((f) => ({ ...f, signature: { ...f.signature, full_name: e.target.value } }))} /></Field>
              <Field label="Title / Role"><Input value={form.signature.title} onChange={(e) => setForm((f) => ({ ...f, signature: { ...f.signature, title: e.target.value } }))} /></Field>
              <Field label="Electronic signature (type name)"><Input data-testid="c-sig-esig" value={form.signature.signature} onChange={(e) => setForm((f) => ({ ...f, signature: { ...f.signature, signature: e.target.value } }))} /></Field>
              <Field label="Date signed"><Input readOnly value={form.signature.signed_at ? new Date(form.signature.signed_at).toLocaleDateString() : new Date().toLocaleDateString()} className="bg-[#FAF8F5]" /></Field>
            </div>
          </Section>

          <div className="flex flex-wrap justify-end gap-2 pt-4 border-t border-[#E8E2D9] mt-4">
            <Button variant="outline" className="rounded-full" onClick={() => setDialogOpen(false)}>Close</Button>
            <Button variant="outline" className="rounded-full" onClick={() => save("draft")} data-testid="c-save-draft">Save draft</Button>
            <Button variant="outline" className="rounded-full" onClick={() => save("sent")} data-testid="c-send">Mark as sent</Button>
            <Button className="rounded-full bg-[#2C3A29] hover:bg-[#3E4C38] text-white" onClick={() => save("signed")} data-testid="c-sign">Sign & activate</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="border border-[#E8E2D9] rounded-2xl p-4 md:p-5 space-y-3 mt-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#57534E]">
        {Icon && <Icon size={14} strokeWidth={1.75} />}{title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function Field({ label, wide, children }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
